import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type ListDealsResult } from '@whinerp/client';
import { EmptyState, ErrorState, Num, Skeleton, type Column } from '@whinerp/ui';
import { formatDate, formatMoney } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { RecordList } from '../shared/record-list.tsx';
import { ViewBar } from '../shared/view-bar.js';
import { useSavedView, visibleColumns } from '../shared/use-saved-view.js';
import { nextSort } from '../shared/use-saved-view.js';

/**
 * SC-57 Deals, as a list.
 *
 * Beside the board, not instead of it. A board is how a pipeline is worked -
 * stage by stage, dragging a card along - and a list is how it is audited:
 * which close this quarter, what the biggest open deal is, everything one
 * person owns. None of those can be asked of columns.
 *
 * `listDeals` had been published, paginated and agent-callable the whole time
 * with no caller anywhere in the product, because `/deals` in the web client
 * is the board and reads `getDealBoard`.
 *
 * The total is of the FILTERED list. A pipeline figure covering the whole
 * table while the rows beneath it are narrowed to one company is right about a
 * question nobody asked - the "correct figure, invisible scope" defect.
 */

type Row = ListDealsResult['deals'][number];

function DealsListScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ field: string; direction: 'asc' | 'desc' }>({
    field: 'closeDate',
    direction: 'asc',
  });
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack[cursorStack.length - 1] ?? null;
  /*
   * The cursor dies when the view or the grouping changes.
   *
   * A cursor is a position in one order. Changing either changes the order, so
   * the page it names is a page of something else - and the person would be
   * looking at rows from the list they just left.
   */
  const savedView = useSavedView(() => setCursorStack([]));

  const query = useQuery<ListDealsResult, ApiError>({
    queryKey: ['deals-list', { search, sort, cursor, view: savedView.params }],
    queryFn: () =>
      api.listDeals({
        query: {
          ...(search.trim() ? { search: search.trim() } : {}),
          ...(cursor ? { cursor } : {}),
          ...savedView.params,
          sort: sort.field,
          direction: sort.direction,
          limit: 50,
        },
      }),
  });

  const columns = useMemo<Column<Row>[]>(
    () => [
      {
        key: 'name',
        header: t('list.column.name'),
        sortField: 'name',
        essential: true,
        cell: (row) => row.name,
      },
      {
        key: 'company',
        header: t('list.column.company'),
        sortField: 'company',
        cell: (row) => row.accountName ?? '',
      },
      {
        key: 'amount',
        header: t('list.column.amount'),
        sortField: 'amount',
        align: 'end',
        essential: true,
        cell: (row) => (
          // Its own currency, not the workspace's: a deal in dollars shown
          // with a dirham sign is a number somebody quotes wrongly.
          <Num align="end" className="text-ink">
            {formatMoney(row.amount, format, { currency: row.currency })}
          </Num>
        ),
      },
      {
        key: 'closeDate',
        header: t('list.column.closeDate'),
        sortField: 'closeDate',
        cell: (row) => (
          <Num className="text-ink-meta">{row.closeDate ? formatDate(row.closeDate, format) : ''}</Num>
        ),
      },
      {
        key: 'owner',
        header: t('list.companies.owner'),
        sortField: 'owner',
        cell: (row) => row.ownerName ?? '',
        hideOnCard: true,
      },
    ],
    [t, format],
  );

  const rows = query.data?.deals ?? [];
  const visible = useMemo(
    () => visibleColumns(columns, query.data?.columns ?? null),
    [columns, query.data?.columns],
  );

  /*
   * What a group heading says, per groupable field.
   *
   * Written out rather than indexed off the row, because the API field name
   * and the row property are deliberately different - `company` is
   * `accountName` - and a lookup by string would be another place the two
   * vocabularies have to agree.
   */
  const groupLabel: Record<string, (row: Row) => string | null> = {
    stage: (row) => row.stageLabel,
    company: (row) => row.accountName,
    owner: (row) => row.ownerName,
  };
  const groupBy = query.data?.groupBy ?? null;
  const grouping = groupBy ? groupLabel[groupBy] : undefined;

  // The groups the server says it will honour, labelled here: it decides what
  // works, the screen decides what it is called.
  const groupNames: Record<string, string> = {
    stage: t('deal.stage'),
    company: t('object.account'),
    owner: t('list.companies.owner'),
  };
  const groupable = (query.data?.groupable ?? [])
    .filter((field) => Object.hasOwn(groupNames, field))
    .map((field) => ({ field, label: groupNames[field]! }));

  return (
    <RecordList<Row>
      title={t('list.deals.title')}
      icon="Target"
      rowId={(row) => row.dealId}
      recordHref={(row) => `/deals/${row.dealId}`}
      columns={visible}
      {...(grouping ? { group: { label: grouping, emptyLabel: t('view.group.empty') } } : {})}
      viewBar={
        <ViewBar
          objectKey="deal"
          active={savedView.active}
          draft={savedView.draft}
          columns={columns.map((column) => ({ key: column.key, label: column.header }))}
          groupable={groupable}
          onChange={savedView.onChange}
          onSelect={savedView.onSelect}
          onRemoveFilter={savedView.onRemoveFilter}
          onDiscard={savedView.onDiscard}
        />
      }
      page={
        query.data
          ? {
              rows,
              total: query.data.total,
              hasMore: query.data.hasMore,
              nextCursor: query.data.nextCursor,
            }
          : undefined
      }
      loading={query.isPending}
      error={query.isError ? { message: query.error.message, rule: query.error.rule } : null}
      onRetry={() => void query.refetch()}
      refreshing={query.isFetching}
      search={search}
      onSearchChange={(value) => {
        setSearch(value);
        setCursorStack([]);
      }}
      sort={sort}
      onSortChange={(field) => {
        setSort((current) => nextSort(current, field));
        setCursorStack([]);
      }}
      cursorStack={cursorStack}
      onCursorStackChange={setCursorStack}
      pageSize={query.data?.limit ?? 50}
      onClearFilters={() => {
        setSearch('');
        setCursorStack([]);
      }}
      filtered={search.trim().length > 0}
      aboveTable={
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          {/*
            * The value of what is listed, with the scope said out loud.
            *
            * "Value of these deals" rather than "Pipeline value": the figure
            * moves with the search box, and a label that did not say so would
            * be a correct number describing something else.
            */}
          <p className="flex items-baseline gap-2">
            <span className="text-micro uppercase tracking-label text-ink-label">
              {t('list.deals.value')}
            </span>
            <Num data-deals-total className="font-serif text-h3 text-ink">
              {query.data?.totalAmount
                ? formatMoney(query.data.totalAmount, format, { currency: rows[0]?.currency ?? 'AED' })
                : '—'}
            </Num>
          </p>
          {/* The board is the other view of the same records, one click away. */}
          <Link data-deals-board to="/deals" className="text-dense text-bronze-text hover:text-ink">
            {t('list.deals.board')}
          </Link>
        </div>
      }
      emptyTitle={t('list.deals.empty.title')}
      emptyBody={t('list.deals.empty.body')}
      emptyIllustration="Target"
    />
  );
}

function DealsListEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="Target"
      title={t('list.deals.empty.title')}
      body={t('list.deals.empty.body')}
      /*
       * The board, not a "New deal" button.
       *
       * A deal is created on the board, where it lands in a stage - creating
       * one from a list would have to ask which stage in a dialog the board
       * answers by being a board. So the next action from an empty list is the
       * view that can make one.
       */
      secondary={{ label: t('list.deals.board'), href: '/deals' }}
    />
  );
}

function DealsListLoading(): JSX.Element {
  return (
    <div className="flex flex-col gap-px" role="status" aria-busy="true">
      <span className="sr-only">Loading deals</span>
      {Array.from({ length: 8 }, (_, index) => (
        <Skeleton key={index} className="h-row-comfortable" />
      ))}
    </div>
  );
}

function DealsListError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="Deals could not be loaded"
      body={error instanceof Error ? error.message : 'The list did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-57',
  route: '/deals/list',
  surface: 'page',
  title: 'Deals list',
  features: ['F-040'],
  entitlement: 'F-040',
  reachedBy: [{ kind: 'screen', from: 'SC-12', how: 'the "Show as a list" link above the board' }],
  components: ['DataTable', 'FilterChips', 'ViewBar'],
  elements: ['RecordList'],
  states: {
    empty: DealsListEmpty,
    loading: DealsListLoading,
    error: DealsListError,
  },
  component: DealsListScreen,
});

export default screen;
